/**
 * Email Domain Blocker
 * Blocks form submissions containing emails from specified domains (e.g., @yopmail.com)
 * Client-side validation that works globally across all forms on the site
 * Note: This can be bypassed by technical users but works for regular form submissions
 */

class EmailDomainBlocker {
  constructor() {
    // Prevent multiple instances
    if (window.emailDomainBlockerInstance) {
      return window.emailDomainBlockerInstance;
    }

    // Get settings from theme customizer (set in theme.liquid)
    const settings = window.emailBlockerSettings || {};

    // Parse blocked domains from settings (comma or newline separated, trimmed, lowercase)
    this.blockedDomains = this.parseDomainsFromSettings(settings.domains);

    // Fallback to default if no domains configured
    if (this.blockedDomains.length === 0) {
      this.blockedDomains = ['yopmail.com'];
    }

    this.errorMessage = settings.errorMessage || 'Please use a valid email address. Temporary email addresses are not allowed.';
    this.isInitialized = false;

    this.init();

    // Store instance globally
    window.emailDomainBlockerInstance = this;
  }

  /**
   * Parse domains from settings array, cleaning whitespace and empty entries
   */
  parseDomainsFromSettings(domains) {
    if (!domains || !Array.isArray(domains)) {
      return [];
    }

    return domains
      .map((domain) => domain.trim().toLowerCase())
      .filter((domain) => domain.length > 0);
  }

  /**
   * Initialize the domain blocker
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    this.setupFormValidation();
    this.setupInputValidation();
    this.isInitialized = true;
  }

  /**
   * Setup form submit validation
   */
  setupFormValidation() {
    document.addEventListener('submit', (event) => {
      this.handleFormSubmit(event);
    }, true); // Use capture phase to intercept before other handlers
  }

  /**
   * Setup real-time input validation for email fields
   */
  setupInputValidation() {
    document.addEventListener('input', (event) => {
      this.handleEmailInput(event);
    });

    document.addEventListener('change', (event) => {
      this.handleEmailInput(event);
    });
  }

  /**
   * Handle form submission and block if blocked domain is found
   */
  handleFormSubmit(event) {
    const form = event.target;

    if (!form || form.tagName !== 'FORM') {
      return;
    }

    const emailInputs = this.getEmailInputs(form);

    for (const input of emailInputs) {
      const email = input.value.trim().toLowerCase();

      if (this.isBlockedDomain(email)) {
        event.preventDefault();
        event.stopPropagation();

        // Show error on input if visible
        this.showError(input);

        // Also show error near submit button for multi-step forms
        this.showSubmitError(form, event);

        // Try to focus the input - if it's hidden (multi-step), this won't work but that's ok
        if (this.isElementVisible(input)) {
          input.focus();
        }
        return;
      }
    }
  }

  /**
   * Check if an element is visible in the DOM
   */
  isElementVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.offsetParent !== null;
  }

  /**
   * Handle email input changes for real-time validation
   */
  handleEmailInput(event) {
    const input = event.target;

    if (!this.isEmailInput(input)) {
      return;
    }

    const email = input.value.trim().toLowerCase();

    if (this.isBlockedDomain(email)) {
      this.showError(input);
      input.setCustomValidity(this.errorMessage);
    } else {
      this.clearError(input);
      input.setCustomValidity('');
    }
  }

  /**
   * Get all email inputs within a form
   */
  getEmailInputs(form) {
    const inputs = [];

    // Get inputs with type="email"
    const emailTypeInputs = form.querySelectorAll('input[type="email"]');
    inputs.push(...emailTypeInputs);

    // Get inputs with email-related names/ids that might be type="text"
    const textInputs = form.querySelectorAll('input[type="text"]');
    textInputs.forEach((input) => {
      const name = (input.name || '').toLowerCase();
      const id = (input.id || '').toLowerCase();
      const placeholder = (input.placeholder || '').toLowerCase();

      if (
        name.includes('email') ||
        id.includes('email') ||
        placeholder.includes('email')
      ) {
        inputs.push(input);
      }
    });

    return inputs;
  }

  /**
   * Check if an input is an email input
   */
  isEmailInput(input) {
    if (!input || input.tagName !== 'INPUT') {
      return false;
    }

    if (input.type === 'email') {
      return true;
    }

    if (input.type === 'text') {
      const name = (input.name || '').toLowerCase();
      const id = (input.id || '').toLowerCase();
      const placeholder = (input.placeholder || '').toLowerCase();

      return (
        name.includes('email') ||
        id.includes('email') ||
        placeholder.includes('email')
      );
    }

    return false;
  }

  /**
   * Check if an email address uses a blocked domain
   */
  isBlockedDomain(email) {
    if (!email || !email.includes('@')) {
      return false;
    }

    const domain = email.split('@')[1];

    if (!domain) {
      return false;
    }

    return this.blockedDomains.some((blockedDomain) => {
      return domain === blockedDomain || domain.endsWith('.' + blockedDomain);
    });
  }

  /**
   * Show error message for an input
   */
  showError(input) {
    // Remove any existing error first
    this.clearError(input);

    // Add error styling to input
    input.classList.add('email-domain-blocked');
    input.setAttribute('aria-invalid', 'true');

    // Create error message element
    const errorId = 'email-domain-error-' + Math.random().toString(36).substr(2, 9);
    const errorElement = document.createElement('div');
    errorElement.id = errorId;
    errorElement.className = 'email-domain-error';
    errorElement.textContent = this.errorMessage;
    errorElement.setAttribute('role', 'alert');

    // Style the error message
    errorElement.style.cssText = `
      color: #c53030;
      font-size: 0.875rem;
      margin-top: 0.25rem;
      display: block;
    `;

    // Insert error message after input
    input.setAttribute('aria-describedby', errorId);
    input.parentNode.insertBefore(errorElement, input.nextSibling);
  }

  /**
   * Clear error message for an input
   */
  clearError(input) {
    input.classList.remove('email-domain-blocked');
    input.removeAttribute('aria-invalid');

    // Remove existing error message
    const existingError = input.parentNode.querySelector('.email-domain-error');
    if (existingError) {
      input.removeAttribute('aria-describedby');
      existingError.remove();
    }
  }

  /**
   * Show error message near the submit button (for multi-step forms)
   */
  showSubmitError(form, event) {
    // Clear any existing submit error
    this.clearSubmitError(form);

    // Find the submit button that was clicked, or find any submit button
    let submitButton = null;

    // Check if the event has a submitter (the button that triggered submit)
    if (event.submitter) {
      submitButton = event.submitter;
    } else {
      // Fallback: find visible submit button
      const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])');
      for (const btn of submitButtons) {
        if (this.isElementVisible(btn)) {
          submitButton = btn;
          break;
        }
      }
    }

    if (!submitButton) return;

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'email-domain-submit-error';
    errorElement.textContent = this.errorMessage;
    errorElement.setAttribute('role', 'alert');

    // Style the error message
    errorElement.style.cssText = `
      color: #c53030;
      font-size: 0.875rem;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
      padding: 0.5rem;
      background-color: #fff5f5;
      border: 1px solid #fc8181;
      border-radius: 4px;
      display: block;
      text-align: center;
    `;

    // Insert error message after submit button
    submitButton.parentNode.insertBefore(errorElement, submitButton.nextSibling);

    // Scroll error into view
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Clear submit error message from form
   */
  clearSubmitError(form) {
    const existingError = form.querySelector('.email-domain-submit-error');
    if (existingError) {
      existingError.remove();
    }
  }
}

// Initialize Email Domain Blocker
if (!window.emailDomainBlockerInstance) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new EmailDomainBlocker();
    });
  } else {
    new EmailDomainBlocker();
  }
}
